<template>
  <button class="swap-button" aria-label="Select a different gift" :disabled="isSwapPreloaderVisible" @click="handleOpenSwapSlideout">
    <Spinner v-if="isSwapPreloaderVisible" />
    <template v-else>
      <span>Swap</span>
      <i class="chevron-large-right-dark-small"></i>
    </template>
  </button>

  <Slideout :id="`swap-${promoId}`" class="swap-slideout" title="Select a gift" @close="handleCloseSlideout(`swap-${promoId}`)">
    <SwapSlideoutContent v-model="localSelectedGiftId" :promo-id="promoId" :product-id="productId" />
    <template #footer>
      <div class="cta-wrapper">
        <button class="primary button expanded" aria-label="Update selected gift with purchase" @click="handleSelectGift(`swap-${promoId}`)">
          Update
        </button>
        <a
          v-if="productUrl"
          :href="productUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="product-details-link link-body margin-top-xs"
          @click="tagViewProductDetailsClickedOnce"
        >
          View Full Product Details
        </a>
      </div>
    </template>
  </Slideout>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import useGWPStore from '~/stores/useGWPStore';
import { useSlideoutStore } from '@nuxt-common/atomic-ui/app/stores/useSlideoutStore';

import Slideout from '@atomic-ui/slideout/Slideout';
import Spinner from '@atomic-ui/spinner/Spinner';
import once from '~/utils/once';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import SwapSlideoutContent from './SwapSlideoutContent.vue';
import { tagGwpSwapDrawerOpened, tagGwpUpdateClicked, tagViewProductDetailsClicked } from '~/features/analytics/gwp';

const {
  promoId,
  productId,
  productUrl = '',
} = defineProps<{
  promoId: string;
  productId: string;
  productUrl?: string;
}>();

const tagGwpSwapDrawerOpenedOnce = once(tagGwpSwapDrawerOpened);
const tagGwpUpdateClickedOnce = once(tagGwpUpdateClicked);
const tagViewProductDetailsClickedOnce = once(tagViewProductDetailsClicked);

const gwpStore = useGWPStore();
const slideoutStore = useSlideoutStore();
const selectedPrice = useProductPrice();

const isSwapPreloaderVisible = ref(false);
const localSelectedGiftId = ref(gwpStore.getSelectedUpcId(promoId));

async function handleOpenSwapSlideout(): Promise<void> {
  try {
    isSwapPreloaderVisible.value = true;

    await gwpStore.fetchGiftInBagStatus({ disableLoadingIndicator: true });

    gwpStore.preselectUpcForEveryPromotion();

    localSelectedGiftId.value = gwpStore.getSelectedUpcId(promoId);

    const { alreadyInBag } = gwpStore.getPropsByPromoId(promoId, selectedPrice.value.lowestSalePriceTier);

    if (alreadyInBag) return;

    slideoutStore.toggle(`swap-${promoId}`);
  } catch (error) {
    console.error('Error fetching UPCs in bag:', error);
  } finally {
    isSwapPreloaderVisible.value = false;
    tagGwpSwapDrawerOpenedOnce();
  }
}

function handleCloseSlideout(id: string): void {
  slideoutStore.close(id);
}

function handleSelectGift(slideoutId: string): void {
  gwpStore.setSelectedUpcId({
    promoId,
    giftProductId: productId,
    giftSelectedUpcId: localSelectedGiftId.value,
    alreadyInBag: false,
  });

  tagGwpUpdateClickedOnce();
  handleCloseSlideout(slideoutId);
}
</script>

<style lang="scss">
@import '~settingSCSS';

dialog.slideout-container.swap-slideout {
  .slideout {
    .slideout-footer {
      @include padding-bottom-s;
    }
  }
}
</style>

<style lang="scss" scoped>
@import '@csgIcons/new/chevron-large-right-dark-small.scss';

.swap-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: rem-calc(12px);
  cursor: pointer;
}

.cta-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
